/**
 * "Privacy and cookie settings" (footer): the one place a visitor changes or withdraws a consent choice.
 *
 * Two consent prompts exist and this reopens both:
 *   - our own banner (CookieConsent), which only covers Google Analytics;
 *   - Google's consent message (AdSense Privacy & messaging, the "googlefc" CMP), which covers
 *     ad cookies. It only loads for visitors in the UK, EEA and Switzerland, so `googlefc` is
 *     simply absent everywhere else and only our banner reopens.
 *
 * The ICO expects withdrawing consent to be as easy as giving it; "clear your cookies" is not.
 */

export const OPEN_COOKIE_SETTINGS_EVENT = 'mcwt:open-cookie-settings';

interface GoogleFc {
  showRevocationMessage?: () => void;
  callbackQueue?: unknown[];
}

export function openCookieSettings(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
  const googlefc = (window as unknown as { googlefc?: GoogleFc }).googlefc;
  // Documented API: clears the stored TCF choice and shows the message again with
  // "Consent", "Do not consent" and "Manage options".
  try {
    googlefc?.showRevocationMessage?.();
  } catch {
    // The CMP is third-party code; a failure there must not break our own banner.
  }
}

type TcfCallback = (tcData: { eventStatus?: string; listenerId?: number } | null, success: boolean) => void;
type TcfApi = (command: string, version: number, callback: TcfCallback, parameter?: unknown) => void;

/**
 * Tells our banner whether Google's consent message is on screen, so the two prompts never stack.
 *
 * Measured 2026-11-01 on the live site (UK, phone): Google's message opened OVER our banner and
 * covered almost the whole screen, leaving only a sliver of our buttons visible: two consent
 * prompts at once. Now our banner steps aside while Google's is showing and comes back once the
 * visitor has answered it (or straight away where Google's message never loads: outside the
 * UK/EEA/Switzerland, or with ads blocked).
 *
 * Uses the documented hooks only: `googlefc.callbackQueue` with CONSENT_API_READY (safe to queue
 * before the CMP loads; it runs the callback once `__tcfapi` exists) and the IAB TCF v2.2
 * `addEventListener` events `cmpuishown` / `useractioncomplete` / `tcloaded`.
 * https://developers.google.com/funding-choices/fc-api-docs
 */
export function watchGoogleConsentMessage(onChange: (open: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const w = window as unknown as { googlefc?: GoogleFc; __tcfapi?: TcfApi };
  let listenerId: number | undefined;
  let stopped = false;

  const listen = () => {
    if (stopped || typeof w.__tcfapi !== 'function') return;
    try {
      w.__tcfapi('addEventListener', 2.2, (tcData, success) => {
        if (!success || !tcData) return;
        if (tcData.listenerId !== undefined) listenerId = tcData.listenerId;
        if (stopped) return;
        if (tcData.eventStatus === 'cmpuishown') onChange(true);
        else if (tcData.eventStatus === 'useractioncomplete' || tcData.eventStatus === 'tcloaded') onChange(false);
      });
    } catch {
      // Third-party CMP: if it misbehaves, our banner simply keeps showing.
    }
  };

  w.googlefc = w.googlefc || {};
  w.googlefc.callbackQueue = w.googlefc.callbackQueue || [];
  w.googlefc.callbackQueue.push({ CONSENT_API_READY: listen });

  return () => {
    stopped = true;
    onChange(false);
    if (listenerId !== undefined && typeof w.__tcfapi === 'function') {
      try {
        w.__tcfapi('removeEventListener', 2.2, () => {}, listenerId);
      } catch {
        // ignore
      }
    }
  };
}
